/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { http, HttpResponse } from 'msw';
import type {
  EventCategoryParams,
  SWEvent,
  TimeseriesResponseData,
} from '../../types';
import {
  interplanetaryMagneticFieldBt,
  interplanetaryMagneticFieldBz,
  kpIndexDummyData,
  kpIndexForecastDummyData,
  solarWindDensityDummyData,
  solarWindPressureDummyData,
  solarWindSpeedDummyData,
  xRayFluxDummyData,
} from '../DummyData';
import {
  fakeBulletins,
  fakeEventList,
  fakeEventListFixedDates,
} from '../fakedata';

export const MOCK_USERNAME = 'user.name';

// get event list /eventlist - possible option to pass: category
const fetchEventList = (
  params = {} as EventCategoryParams,
  fixedDates = false,
): SWEvent[] => {
  const list = fixedDates ? fakeEventListFixedDates : fakeEventList;
  if (params?.category) {
    return list.filter((event) => event.category === params.category);
  }
  if (params.originator && params.originator === 'KNMI') {
    return list.filter((event) => event.originator === 'KNMI');
  }
  return list;
};

export const getDummyTimeSerie = (
  stream: string,
  parameter: string,
): TimeseriesResponseData => {
  switch (stream) {
    case 'kpforecast':
      return kpIndexForecastDummyData;
    case 'rtsw_mag':
      return parameter === 'bt'
        ? interplanetaryMagneticFieldBt
        : interplanetaryMagneticFieldBz;
    case 'rtsw_wind': {
      if (parameter === 'proton_speed') {
        return solarWindSpeedDummyData;
      }
      if (parameter === 'proton_pressure') {
        return solarWindPressureDummyData;
      }
      if (parameter === 'proton_density') {
        return solarWindDensityDummyData;
      }
      return solarWindDensityDummyData;
    }
    case 'xray':
      return xRayFluxDummyData;
    default:
      return kpIndexDummyData;
  }
};

// Bulletin section
export const bulletinEndpoints = [
  http.get(/.*\/bulletin\/getBulletin$/, () => {
    return HttpResponse.json(fakeBulletins[0]);
  }),
  http.get(/.*\/bulletin\/getBulletinHistory$/, () => {
    return HttpResponse.json(fakeBulletins);
  }),
];

// Notification section
export const notificationEndpoints = [
  http.get(/.*\/notification\/eventList$/, ({ request }) => {
    const url = new URL(request.url);
    const params: Record<string, string> = {};
    for (const [key, value] of url.searchParams.entries()) {
      params[key] = value;
    }

    return HttpResponse.json(fetchEventList(params as EventCategoryParams));
  }),
  http.get(/.*\/notification\/event\//, ({ request }) => {
    const url = new URL(request.url);
    const eventid = url.pathname.split('/').pop();
    return HttpResponse.json(
      fakeEventList.find((fakeEvent) => fakeEvent.eventid === eventid),
    );
  }),
  http.get(/.*\/notification\/newNotifications$/, () => {
    return HttpResponse.json(
      fakeEventList.filter(
        (event) => event.originator === 'METOffice' && event.notacknowledged,
      ),
    );
  }),
  http.post(/.*\/notification\/store$/, ({ request }) => {
    return HttpResponse.json(request.formData);
  }),
  http.post(/.*\/notification\/discard/, () => {
    return HttpResponse.json();
  }),
  http.post(/.*\/notification\/acknowledged\//, () => {
    return HttpResponse.json();
  }),
  http.post(/.*\/notification\/populate$/, () => {
    return HttpResponse.json({
      title: 'Template title for you',
      message: 'Template message text for you',
    });
  }),
];

export const fixedNotificationDateEndpoint = [
  http.get(/.*\/notification\/event\//, ({ request }) => {
    const url = new URL(request.url);
    const eventid = url.pathname.split('/').pop();
    return HttpResponse.json(
      fakeEventListFixedDates.find(
        (fakeEvent) => fakeEvent.eventid === eventid,
      ),
    );
  }),
];

export const timeseriesEndpoints = [
  http.get(/.*\/timeseries\/streams$/, () => {
    return HttpResponse.json(null);
  }),
  http.get('/timeseries/data', ({ request }) => {
    const url = new URL(request.url);
    const params: Record<string, string> = {};
    for (const [key, value] of url.searchParams.entries()) {
      params[key] = value;
    }

    return HttpResponse.json(
      getDummyTimeSerie(params.stream, params.parameter),
    );
  }),
];

export const requestHandlers = [
  ...bulletinEndpoints,
  ...notificationEndpoints,
  ...timeseriesEndpoints,
];
